import React from 'react';
import SchemaTyped from 'rsuite/esm/Schema/Schema';
import FormTextField from '../components/form/FormTextField';
import AnnotatedDatePicker from '../components/form/AnnotatedDatePicker';
import RadioQuestionGroup from '../components/form/RadioQuestionGroup';
import { constructMarkup } from './utils';
import { Text } from 'rsuite';
import { CustomHTML } from '../components/common/CustomHTML';

export const getValidatorConfigurationForAnswerType = (
  answerType,
  isRequired = false
) => {
  const { StringType, NumberType } = SchemaTyped.Types;
  let validatorConfig = {
    validator: null,
    defaultErrorMessage: '',
  };
  switch (answerType) {
    case 'positive_number':
      validatorConfig.validator = NumberType().min(0);
      validatorConfig.defaultErrorMessage = 'Must be a positive number';
      break;
    case 'boolean':
      validatorConfig.validator = StringType().addRule(
        (value) => value === 'Yes' || value === 'No',
        null
      );
      validatorConfig.defaultErrorMessage = 'Selection is required';
      break;
    case 'string':
    default:
      validatorConfig.validator = StringType();
      validatorConfig.defaultErrorMessage = 'Must be text.';
  }

  if (isRequired && validatorConfig.validator) {
    validatorConfig.validator = validatorConfig.validator.isRequired();
  }
  return validatorConfig;
};

export const getConditionsConfiguration = (question) => {
  const conditionDescriptions = question.conditions;
  const conditionConfigs = conditionDescriptions?.map((condition) => {
    return {
      compareKey: condition.key,
      evaluationFn: mapConditionDescriptionToCallback(condition),
    };
  });
  return conditionConfigs;
};

const mapConditionDescriptionToCallback = (description) => {
  switch (description.op) {
    case 'equal':
      return (val) => val === description.value;
    case 'notEqual':
      return (val) => val !== description.value;
    case 'jsonEq':
      return (val) => JSON.stringify(val) === JSON.stringify(description.value);
    case 'notjsonEq':
      return (val) => JSON.stringify(val) !== JSON.stringify(description.value);
    case 'isNumberUnset':
      return (val) => val === undefined || val === null;
    case 'isDefined':
      return (val) => val !== undefined && val !== null;
    case 'isTruthy':
      return (val) => val == description.value;
    case 'isFalsy':
      return (val) => val != description.value;
    case 'any':
      return (val) =>
        !Array.isArray(description.value) ||
        (Array.isArray(description.value) && description.value.includes(val));
    case 'noneOf':
      return (val) =>
        !Array.isArray(description.value) ||
        (Array.isArray(description.value) && !description.value.includes(val));
    case 'dateIsBefore':
      return (val) =>
        val instanceof Date &&
        description.value instanceof Date &&
        val.getTime() < description.value.getTime();
    case 'dateIsAfter':
      return (val) =>
        val instanceof Date &&
        description.value instanceof Date &&
        val.getTime() > description.value.getTime();
    case 'dateIsSame':
      return (val) =>
        val instanceof Date &&
        description.value instanceof Date &&
        val.getTime() === description.value.getTime();
    case 'greaterThan':
      return (val) => val > description.value;
    case 'greaterThanOrEqual':
      return (val) => val >= description.value;
    case 'lessThan':
      return (val) => val < description.value;
    case 'lessThanOrEqual':
      return (val) => val <= description.value;
    default:
      return () => null;
  }
};

export const mapQuestionToComponent = (
  question,
  formValues,
  formError,
  updateFormValues,
  onBooleanRadioQuestionGroupChanged,
  answerRequired = true
) => {
  // Get form values has data stored for this question
  const existingData = (formValues && formValues[question.key]) ?? '';

  // Check whether their are conditions that constrain whether the control should be shown
  const conditionConfig =
    question?.conditions?.length > 0
      ? getConditionsConfiguration(question)
      : null;
  // Determine if all conditions (if any) have been meet such that the control should render
  const unmetRenderConditions =
    conditionConfig?.map((configConditions) => {
      if (!formValues) {
        return false;
      }
      return configConditions.evaluationFn(
        formValues[configConditions.compareKey]
      );
    }) ?? [];

  // Only render if there are no unmet render conditions
  if (unmetRenderConditions.includes(false)) return;

  // Get the appropriate validator for the question type
  const validationConfig = getValidatorConfigurationForAnswerType(
    question.answer_type,
    answerRequired
  );

  // Form Text Field components
  if (
    question.answer_type === 'positive_number' ||
    question.answer_type === 'string' ||
    question.answer_type === 'currency' ||
    question.answer_type === 'text_area'
  ) {
    return (
      <FormTextField
        key={`text_field_${question.key}`}
        name={question.key}
        label={<CustomHTML text={question.title} />}
        boldLabel
        subtitle={() => (
          <Text className={['mb-8'].join(' ')}>
            <CustomHTML text={question.description} />
          </Text>
        )}
        hasError={formError[question.key]}
        validationRule={validationConfig.validator}
        errorMessage={validationConfig.defaultErrorMessage}
        onChange={(v) => updateFormValues(question.key, v)}
        value={existingData}
        inputWidth={'100%'}
        className="mb-16"
        inputFieldType={`${question.answer_type === 'text_area' ? 'textarea' : 'text'}`}
        useGridSizing
      />
    );
  }

  // Boolean Yes/No radio button options
  if (question.answer_type === 'boolean' || question.answer_type === 'radio') {
    const options = question.radio_options
      ? Array.from(question.radio_options)
      : [
          { key: 'Yes', label: 'Yes' },
          { key: 'No', label: 'No' },
        ];

    const selectedOption = options.find(
      (option) => option.key === existingData
    );

    // Filter Options to display just the one that matches the conditions given
    const filteredOptions = options.filter((option) => {
      if (!option.conditions) {
        return true;
      }

      const conditions = getConditionsConfiguration(option);
      return conditions
        ?.map((condition) => {
          if (!formValues) {
            return false;
          }

          return condition.evaluationFn(formValues[condition.compareKey]);
        })
        .some((value) => value);
    });

    return (
      <RadioQuestionGroup
        key={`radio_group_${question.key}`}
        name={`${question.key}`}
        title={<CustomHTML text={question.title} />}
        description={<CustomHTML text={question.description} />}
        options={filteredOptions}
        defaultOption={selectedOption}
        value={selectedOption}
        onChange={(value) =>
          onBooleanRadioQuestionGroupChanged(value, question, question.key)
        }
        hasError={formError[`${question.key}`]}
        validationRule={validationConfig.validator}
        errorMessage={validationConfig.defaultErrorMessage}
        className="mb-4"
      />
    );

    // Left the code below commented out but it may be needed since we already have a "radio" option in
    // the config and it is a bit strange to treat the "boolean" option as a radio component.

    // return (
    //     <AnnotatedCheckbox
    //         style={{marginTop: 0, marginBottom: "12px"}}
    //         name={question.key}
    //         label={question.title}
    //         onChange={(checked) => { updateFormValues(question.key, checked ? 'Yes' : 'No') }}
    //     />
    // )
  }

  // Date fields
  if (question.answer_type === 'date') {
    return (
      <AnnotatedDatePicker
        key={`tdate_picker_${question.key}`}
        name={question.key}
        label={question.title}
        subtitle={() => (
          <Text className={['mb-8'].join(' ')}>
            {constructMarkup(question.description)}
          </Text>
        )}
        format={'MM-dd-yyyy'}
        dateConvertor={(date) => {
          if (!date) return;
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }}
        onChange={(date) => updateFormValues(question.key, date)}
      />
    );
  }
};
